import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link2, X, Plus, UserCircle2, Castle, Flag, CornerUpLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
  { type: "npcs", label: "NPCs", field: "linked_npcs", icon: UserCircle2 },
  { type: "locations", label: "Locations", field: "linked_locations", icon: Castle },
  { type: "factions", label: "Factions", field: "linked_factions", icon: Flag },
];
const ICONS = { npcs: UserCircle2, locations: Castle, factions: Flag };

/**
 * Related-entities panel (Phase 6). Shows curated links to NPCs / Locations /
 * Factions plus an auto "Linked from" reverse list, with click-to-navigate.
 */
export const RelatedEntitiesPanel = ({ entityType, entityId, canEdit = true }) => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ outgoing: { npcs: [], locations: [], factions: [] }, incoming: [] });
  const [options, setOptions] = useState({ npcs: [], locations: [], factions: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/links/${entityType}/${entityId}`, { withCredentials: true });
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [campaignId, entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [n, l, f] = await Promise.all([
          axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }),
          axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
          axios.get(`${API}/campaigns/${campaignId}/factions`, { withCredentials: true }),
        ]);
        if (!cancelled) setOptions({
          npcs: n.data.map(x => ({ id: x.id, name: x.name })),
          locations: l.data.map(x => ({ id: x.id, name: x.name })),
          factions: f.data.map(x => ({ id: x.id, name: x.name })),
        });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  const routeFor = (type, id) => type === "locations"
    ? `/campaign/${campaignId}/locations/${id}`
    : `/campaign/${campaignId}/${type}?focus=${id}`;

  const save = async (field, ids) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/links/${entityType}/${entityId}`, { [field]: ids }, { withCredentials: true });
      load();
    } catch { toast.error("Failed to update links"); }
  };

  const addLink = (group, id) => {
    if (!id) return;
    const current = data.outgoing[group.type].map(x => x.id);
    if (current.includes(id)) return;
    save(group.field, [...current, id]);
  };
  const removeLink = (group, id) => {
    const current = data.outgoing[group.type].map(x => x.id);
    save(group.field, current.filter(x => x !== id));
  };

  const hasAny = GROUPS.some(g => data.outgoing[g.type].length > 0) || data.incoming.length > 0;
  if (loading && !hasAny) {
    return <div className="text-xs text-muted-foreground py-2" data-testid="related-loading">Loading links…</div>;
  }

  return (
    <div className="space-y-3" data-testid="related-entities-panel">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
        <Link2 className="w-3.5 h-3.5" /> Related
      </div>

      {GROUPS.map((g) => {
        const linked = data.outgoing[g.type];
        const linkedIds = new Set(linked.map(x => x.id));
        const avail = options[g.type].filter(o => !linkedIds.has(o.id) && !(entityType === g.type && o.id === entityId));
        const Icon = g.icon;
        if (!canEdit && linked.length === 0) return null;
        return (
          <div key={g.type} data-testid={`related-group-${g.type}`}>
            <div className="flex items-center gap-1.5 text-[11px] text-foreground/70 mb-1"><Icon className="w-3 h-3" /> {g.label}</div>
            <div className="flex flex-wrap gap-1.5">
              {linked.map((it) => (
                <Badge key={it.id} variant="outline" className="cursor-pointer border-primary/30 hover:bg-primary/10 group pr-1" data-testid={`related-chip-${g.type}-${it.id}`}>
                  <span onClick={() => navigate(routeFor(g.type, it.id))} className="mr-1">{it.name}</span>
                  {canEdit && (
                    <button onClick={(e) => { e.stopPropagation(); removeLink(g, it.id); }} className="opacity-50 hover:opacity-100" data-testid={`related-remove-${g.type}-${it.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </Badge>
              ))}
              {linked.length === 0 && !canEdit && <span className="text-xs text-muted-foreground italic">None</span>}
            </div>
            {canEdit && avail.length > 0 && (
              <Select value="" onValueChange={(v) => addLink(g, v)}>
                <SelectTrigger className="h-8 mt-1.5 text-xs bg-input border-border rounded-sm w-full max-w-[240px]" data-testid={`related-add-${g.type}`}>
                  <div className="flex items-center gap-1 text-muted-foreground whitespace-nowrap overflow-hidden"><Plus className="w-3 h-3 shrink-0" /> <span className="truncate">Link {g.label.toLowerCase()}…</span></div>
                </SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-64">
                  {avail.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        );
      })}

      {data.incoming.length > 0 && (
        <div data-testid="related-incoming">
          <div className="flex items-center gap-1.5 text-[11px] text-foreground/70 mb-1 mt-2"><CornerUpLeft className="w-3 h-3" /> Linked from</div>
          <div className="flex flex-wrap gap-1.5">
            {data.incoming.map((it) => {
              const Icon = ICONS[it.type] || Link2;
              const canNav = it.type === "npcs" || it.type === "locations" || it.type === "factions";
              return (
                <Badge key={`${it.type}-${it.id}`} variant="outline"
                  className={`gap-1 border-border/60 text-muted-foreground ${canNav ? "cursor-pointer hover:bg-primary/10 hover:text-foreground" : ""}`}
                  onClick={() => canNav && navigate(routeFor(it.type, it.id))}
                  data-testid={`related-incoming-${it.type}-${it.id}`}>
                  <Icon className="w-3 h-3 opacity-60" /> {it.name}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedEntitiesPanel;
